import React, { useCallback, useEffect, useState, useContext, createContext } from 'react';
import { MainTracker } from '../PixelTrackers/MainTracker';
import { resetCustomData, setCustomData, trackGoal } from '../PixelTrackers/KameleoonTracker';
import { track as trackGTM } from "../PixelTrackers/GtmTracker";
import * as http from "../utils/http";



const GENDER_TO_STRING: Record<string, string> = {
  "0": "female",
  "1": "male",
}

// Paid orders before the current one (customer-details response, HL-12604 Kameleoon customer_type).
interface CustomerNumbers {
  customer_paid_orders?: number | null;
  customer_ltv_eur?: number | null;
  days_since_last_order?: number | null;
}

const CUSTOMER_NUMBER_KEYS: (keyof CustomerNumbers)[] = ["customer_paid_orders", "customer_ltv_eur", "days_since_last_order"];

// Define the customer data interface
interface CustomerData extends CustomerNumbers {
  customerID?: string;
  uuid?: string;
  token?: string;           // Deprecated, but kept for backward compatibility
  email?: string;
  fullName?: string;
  optForSendables?: string | null;
  loginSource?: string;
  provider?: string;
}

// Define a more specific type for saveCustomer parameters
interface SaveCustomerParams {
  customerID: string;
  uuid: string;
  token?: string;           // Deprecated, but kept for backward compatibility
  email?: string;
  fullName?: string;
  optForSendables?: string | null;
  loginSource?: string;
  provider?: string;
  isSignup ?: boolean // If we saving the customer because of a signup and not login.
  gender?: number;
  lastPaidCategoryMention?: string;
}

interface CustomerContextType {
    customerState: CustomerData | null;
    getCustomerState: () => CustomerData | null;
    logout: (redirectUrl?: string) => void;
    saveCustomer: (data: SaveCustomerParams) => CustomerData;
}

// Create the context
const CustomerContext = createContext<CustomerContextType | undefined>(undefined);

// Custom hook to use the context
export function useCustomer(): CustomerContextType {
  const context = useContext(CustomerContext);
  if (!context) {
    throw new Error("useCustomer must be used within a CustomerProvider");
  }
  return context;
}

const storeCustomerNumbers = (numbers: CustomerNumbers) => {
  CUSTOMER_NUMBER_KEYS.forEach((key) => window.eventsTracking.setData(key, numbers[key] ?? "", true));
};

const readCustomerNumbers = (): CustomerNumbers => {
  const numbers: CustomerNumbers = {};
  CUSTOMER_NUMBER_KEYS.forEach((key) => {
    const value = window.eventsTracking.getData(key);
    if (value !== "" && value != null) numbers[key] = Number(value);
  });
  return numbers;
};

// Kameleoon customer custom data from the session store; called on every route change (PageTracker),
// after login / logout and after the customer-details refresh. Never on the thank-you page: the numbers
// describe the customer before the current order.
export const setKameleoonCustomerData = () => {
  if (window.location.pathname.split("/").includes("thank-you")) return;
  // token and customerID, like the CMS: a CMS logout clears only the token
  const loggedIn = Boolean(window.eventsTracking.getData("token") && window.eventsTracking.getData("customerID"));
  setCustomData("is_logged_in", loggedIn);
  if (!loggedIn) {
    setCustomData("customer_type", "unknown");
    // Visitor-scope values stay until overwritten: clear the last customer's numbers back to "No value".
    CUSTOMER_NUMBER_KEYS.forEach(resetCustomData);
    return;
  }
  const { customer_paid_orders, customer_ltv_eur, days_since_last_order } = readCustomerNumbers();
  if (customer_paid_orders == null) return;
  setCustomData("customer_type", customer_paid_orders > 0 ? "rp" : "ftb");
  setCustomData("customer_paid_orders", customer_paid_orders);
  if (customer_ltv_eur != null) setCustomData("customer_ltv_eur", customer_ltv_eur);
  else resetCustomData("customer_ltv_eur");
  // no paid order yet: no days value (also clears a previous account's value)
  if (days_since_last_order != null) setCustomData("days_since_last_order", days_since_last_order);
  else resetCustomData("days_since_last_order");
};

// Load customer data from tracking
export const loadCustomerFromSession = (): CustomerData | null => {
  
  const token = window.eventsTracking.getData("token");
  const customerID = window.eventsTracking.getData("customerID");
  const uuid = window.eventsTracking.getData("uuid");
  const email = window.eventsTracking.getData("email");
  const fullName = window.eventsTracking.getData("full_name");
  const optForSendables = window.eventsTracking.getData("opt_for_sendables");
  const provider = window.eventsTracking.getData("provider");
  

  if (token) {
    return {
      token,
      customerID,
      uuid,
      email,
      fullName,
      optForSendables,
      provider,
      ...readCustomerNumbers(),
    };
  }
  
  return null;
};

// Clear customer session data
export const clearCustomerFromSession = () => {
  window.eventsTracking.setData("token", "", true);
  window.eventsTracking.setData("customerID", "", true);
  window.eventsTracking.setData("uuid", "", true);
  window.eventsTracking.setData("email", "", true);
  window.eventsTracking.setData("full_name", "", true);
  window.eventsTracking.setData("opt_for_sendables", "", true);
  window.eventsTracking.setData("provider", "", true);
  window.eventsTracking.setData("gender", "", true);
  window.eventsTracking.setData("last_paid_category", "", true);
  storeCustomerNumbers({});
};


// Provider component
export const CustomerProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  
  // Initialize state with tokens from URL or session
  const [customerState, setCustomerState] = useState<CustomerData | null>(loadCustomerFromSession());

  const logout = useCallback((redirectUrl?: string) => {
    // Clear all customer data
    clearCustomerFromSession();
    // Track logout in MixPanel
    MainTracker.superProperties({"Is Logged In?": "No"});
    setKameleoonCustomerData();
    if (redirectUrl) {
      window.location.href = redirectUrl;
    } else {
      // Reset customer state
      setCustomerState(null);
    }
  }, []);

  const getCustomerState = useCallback(() => {
    return customerState;
  }, [customerState]);

  // Customer numbers from the server, so a returning session is fresh (e.g. logged in, bought, came back).
  const refreshCustomerNumbers = () => {
    http.getFromServer("customer-details", [http.MIDDLEWARES.CUSTOMER_ID])
      .then((response: { data: CustomerNumbers }) => {
        storeCustomerNumbers(response.data);
        setCustomerState((current) => current && { ...current, ...readCustomerNumbers() });
        setKameleoonCustomerData();
      })
      .catch((err: unknown) => window.clientLogger.error("customer-details refresh failed", err));
  };

  // App boot with a saved login
  useEffect(() => {
    if (customerState?.token) refreshCustomerNumbers();
  }, []);

  const saveCustomer = (data: SaveCustomerParams) => {


    // Store in window tracking
    window.eventsTracking.setData("customerID", data.customerID, true);
    window.eventsTracking.setData("full_name", data.fullName || "", true);
    window.eventsTracking.setData("token", data.token || "", true);
    window.eventsTracking.setData("email", data.email || "", true);
    window.eventsTracking.setData("gender", data.gender ? GENDER_TO_STRING[data.gender.toString()] : "", true);
    window.eventsTracking.setData("last_paid_category", data.lastPaidCategoryMention || "", true);
    window.eventsTracking.setData("uuid", data.uuid, true);
    window.eventsTracking.setData("opt_for_sendables", data.optForSendables?.toString() || "", true);
    window.eventsTracking.setData("provider", data.provider || "local", true);
    refreshCustomerNumbers();

    //update oneSignal user
    window.OneSignalDeferred = window.OneSignalDeferred || [];
    window.OneSignalDeferred.push(async function(OneSignal: any) {
      await OneSignal.login(data.uuid);
    })
    
    // Track in MixPanel
    MainTracker.identify(data.uuid, data.email || '');
    MainTracker.people.set();
    MainTracker.superProperties({"Is Logged In?": "Yes"});
    if (data.isSignup) trackGoal("sign_up");

    //Gtm Track:
    trackGTM("custom", "ga4_auto_event", {
      event_name: data.isSignup ? "sign_up" : 'login', 
      provider: data.provider,
      user_data: {
        user_id: data.customerID,
        customer_type : data.isSignup ? "FTB" : "RP", 
      }
    });

    
    // Update internal state with merged data
    const updatedData = {
      ...data,
    };
    
    setCustomerState(updatedData);
    return updatedData;
  }

  return (
    <CustomerContext.Provider value={{
        logout,
        saveCustomer,
        customerState,
        getCustomerState,
    }}>
      {children}
    </CustomerContext.Provider>
  );
}; 